import { useState } from 'react';

import type { MarketingNote } from '../../../../api/venue-marketing';

import { NoteActions } from './NoteActions';
import { NoteBody } from './NoteBody';
import { NoteByline } from './NoteByline';
import { NoteDoneControl } from './NoteDoneControl';
import { NoteTrail } from './NoteTrail';
import { useFocusReturn } from './use-focus-return';
import { useNoteActions } from './use-note-actions';

interface MarketingNoteItemProps {
  note: MarketingNote;
  venueId: string;
  /** The venue's region, named in a refusal. */
  regionName: string | null;
  /** Whether the caller may tick action items done. */
  canToggle: boolean;
  last: boolean;
  onError: (message: string) => void;
}

/** One note or action item: who wrote it, its text as plain text, and what can be done to it. */
export function MarketingNoteItem({
  note,
  venueId,
  regionName,
  canToggle,
  last,
  onError,
}: MarketingNoteItemProps) {
  const actions = useNoteActions(venueId, onError, regionName);
  const [editing, setEditing] = useState(false);
  const editButton = useFocusReturn();
  const isAction = note.kind === 'ACTION_ITEM';
  const toggle = () => void actions.toggleDone(note);
  const closeEditor = () => {
    setEditing(false);
    editButton.request();
  };

  return (
    <li
      style={{
        display: 'flex',
        gap: 10,
        padding: '10px 0',
        borderBottom: last ? 'none' : '1px solid var(--nb-border)',
        opacity: note.isDone ? 0.8 : 1,
        listStyle: 'none',
      }}
    >
      {isAction && (
        <div style={{ paddingTop: 2 }}>
          <NoteDoneControl
            done={note.isDone}
            canToggle={canToggle}
            onToggle={toggle}
            busy={actions.busy}
          />
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'nowrap' }}>
          <NoteByline note={note} />
          {note.canEdit && !editing && (
            <NoteActions
              editButtonRef={editButton.ref}
              onEdit={() => setEditing(true)}
              onDelete={() => void actions.remove(note)}
              busy={actions.busy}
            />
          )}
        </div>
        <NoteBody
          note={note}
          editing={editing}
          busy={actions.busy}
          onCancel={closeEditor}
          onSave={(body) => void actions.saveBody(note, body).then((ok) => ok && closeEditor())}
        />
        <NoteTrail note={note} onReopen={canToggle && isAction ? toggle : undefined} />
      </div>
    </li>
  );
}
